import { PartnerSettingValue, RecordFolderAssignment } from '@provision/entity-types';
import { useQuery } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { RULES_MAP_PER_VIEW_ENTITY, useCustomViews } from 'src/graphql/entities/custom-view';
import { useGroupRules } from 'src/graphql/entities/group-rules';
import { isSettingsEnabled, usePartnerSettings } from 'src/graphql/entities/partner-setting';
import { GET_RECORD_FOLDER_ASSIGNMENT_QUERY } from 'src/graphql/entities/record-folder-assignment/record-folder-assignment.operations';
import { EntityNames, QueryParams, QueryVariables, Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { useUpdateRecordFolderAssignment } from './use-update-record-folder-assignment';

// instead of adding pagination, we just limit the number of records that can be assigned to avoid slack errors
// we fetch "all" once and then operate quickly on the client side without hundreds of API requests
const MAX_OPERABLE_RECORDS = 2000;

export interface GetRecordFolderAssignment {
  getRecordFolderAssignment: Records<RecordFolderAssignment>;
}

interface UseRecordFolderAssignmentsOptions {
  entityFilter?: string[];
  skip?: boolean;
}

export const useRecordFolderAssignments = (options?: UseRecordFolderAssignmentsOptions) => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const entityFilter = options?.entityFilter;
  const skip = options?.skip;

  const { data, previousData, loading } = useQuery<GetRecordFolderAssignment, QueryVariables>(
    GET_RECORD_FOLDER_ASSIGNMENT_QUERY,
    {
      variables: {
        params: getRecordFolderAssignmentQueryParams(entityFilter),
      },
      skip,
      context: {
        includePlatformLevelData: true,
      },
    },
  );

  const { data: partnerSettings } = usePartnerSettings({ partnerId: selectedPartnerId });
  const { groupRules } = useGroupRules();
  const { customViews } = useCustomViews({ partnerId: selectedPartnerId });

  const recordFolderAssignments = useMemo<RecordFolderAssignment[]>(() => {
    const list =
      data?.getRecordFolderAssignment?.records ??
      previousData?.getRecordFolderAssignment?.records ??
      [];

    return list
      .filter((i) => {
        const entityName = (
          i.entity === 'CustomView'
            ? customViews.find((view) => view.id === i.entityId)?.entityName
            : i.entity.toLowerCase()
        ) as EntityNames;

        if (!entityName) {
          return false;
        }

        const restrictions = RULES_MAP_PER_VIEW_ENTITY[entityName];

        if (!restrictions) {
          return true;
        }

        const { allowedRules, disallowedPartnerSettings = [] } = restrictions;

        const rulesMatch = !allowedRules || allowedRules.some((rule) => groupRules[rule]);
        if (!rulesMatch) {
          return false;
        }

        const settingsDisallowMatch =
          disallowedPartnerSettings.some((settingId) =>
            partnerSettings?.some((ps) =>
              isSettingsEnabled(settingId, ps as PartnerSettingValue),
            ),
          ) ?? false;

        return !settingsDisallowMatch;
      })
      .sort(
        (a, b) =>
          a.sortOrder - b.sortOrder ||
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
  }, [
    data?.getRecordFolderAssignment?.records,
    previousData?.getRecordFolderAssignment?.records,
    groupRules,
    partnerSettings,
    customViews,
  ]);

  const { updateRecordFolderAssignment } = useUpdateRecordFolderAssignment();

  const reorderAssignments = useCallback(
    async (navSubItems: NavigationDataItem[]) => {
      const orderedItems = navSubItems.map((i) => i.rawAssignmentItem!).filter(Boolean);

      await Promise.all(
        orderedItems.map((item, index) => {
          if (item.sortOrder === index) {
            return;
          }

          return updateRecordFolderAssignment(item, { sortOrder: index });
        }),
      );
    },
    [updateRecordFolderAssignment],
  );

  return {
    recordFolderAssignments,
    reorderAssignments,
    isLoading: loading,
  };
};

export function getRecordFolderAssignmentQueryParams(entityFilter?: string[]): QueryParams {
  const filters = entityFilter?.length
    ? [
        {
          attribute: 'entity',
          operator: 'in',
          value: entityFilter.join(','),
        },
      ]
    : undefined;

  return {
    ...(filters ? { filters } : {}),
    offset: 0,
    limit: MAX_OPERABLE_RECORDS,
  };
}
